import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Webchat de Botmaker (asistente UAI) con contexto de página (T-2026-197).
// El puente uad-botmaker-bridge lee las variables `seccion` y `tema` del chat y, en el primer
// mensaje de cada página, le antepone al agente "El usuario se encuentra en la sección {seccion}
// y necesita asistencia sobre {tema}…" (T-2026-194). Las variables tienen que estar en
// window.BOTMAKER_VAR ANTES de cargar init.js: el widget las lee al iniciar.
// Sin REACT_APP_BOTMAKER_WEBCHAT_ID no se carga nada (así se apaga por entorno).
//
// Burbuja movible (T-2026-207, propuesta de Maximiliano Bonaccorsi): en pantallas chicas la burbuja
// tapa el formulario. Botmaker dibuja la burbuja y el panel dentro de un iframe (los eventos de
// mouse/touch de adentro no llegan a la página), así que se agrega un "agarre" en el documento
// que arrastra el contenedor completo; el clic en la burbuja sigue abriendo el chat.
// El contenedor no tiene id: es el padre de iframe[name="Botmaker"]. Botmaker reescribe
// bottom/right/left en su style al abrir y cerrar el chat (aunque tengan !important), por eso la
// posición se vuelve a aplicar ante cada cambio de style o de tamaño.
const WEBCHAT_ID = process.env.REACT_APP_BOTMAKER_WEBCHAT_ID;
const SCRIPT_ID = 'botmaker-webchat-init';
const STORAGE_KEY = 'botmaker-launcher-pos';
const HANDLE_SIZE = 24;
const KEY_STEP = 16;
const MARGIN = 8;

export const CONTEXTO_CHECKOUT = {
  seccion: 'checkout',
  tema: 'el formulario de inscripción',
};

const findContainer = () => document.querySelector('iframe[name="Botmaker"]')?.parentElement || null;

const readPos = () => {
  try {
    const p = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return typeof p?.x === 'number' && typeof p?.y === 'number' ? p : null;
  } catch {
    return null;
  }
};

const savePos = (pos) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    // modo privado o storage bloqueado: la posición dura solo esta visita
  }
};

// Mantiene el widget dentro de la ventana con su tamaño actual (burbuja o panel abierto).
// Si no entra (panel de 450px en un celular angosto), se ancla abajo a la derecha como lo deja
// Botmaker: ahí están la burbuja y el botón de cerrar, que tienen que quedar a la vista.
const clampEje = (valor, tamanio, ventana) => {
  const max = ventana - tamanio - MARGIN;
  return max < MARGIN ? max : Math.min(Math.max(MARGIN, valor), max);
};

const clamp = (pos, el) => {
  const r = el.getBoundingClientRect();
  return {
    x: clampEje(pos.x, r.width, window.innerWidth),
    y: clampEje(pos.y, r.height, window.innerHeight),
  };
};

const PROPS = { left: (p) => `${p.x}px`, top: (p) => `${p.y}px`, right: () => 'auto', bottom: () => 'auto' };

// Solo escribe lo que cambió: el MutationObserver de style no entra en bucle.
const applyPos = (el, pos) => {
  Object.entries(PROPS).forEach(([prop, valor]) => {
    const v = valor(pos);
    if (el.style.getPropertyValue(prop) !== v || el.style.getPropertyPriority(prop) !== 'important') {
      el.style.setProperty(prop, v, 'important');
    }
  });
};

const BotmakerWebchat = () => {
  const [container, setContainer] = useState(null);
  const [shown, setShown] = useState(null);   // posición aplicada (ajustada a la ventana)
  const wantedRef = useRef(null);             // posición elegida por el usuario
  const shownRef = useRef(null);
  const dragRef = useRef(null);

  // Aplica la posición elegida, ajustada al tamaño actual. La elegida no se toca: al cerrar el
  // panel la burbuja vuelve adonde la dejó el usuario.
  const render = useCallback((el) => {
    if (!wantedRef.current) return;
    const c = clamp(wantedRef.current, el);
    applyPos(el, c);
    if (shownRef.current?.x !== c.x || shownRef.current?.y !== c.y) {
      shownRef.current = c;
      setShown(c);
    }
  }, []);

  // 1) Cargar el script y esperar el contenedor que inyecta Botmaker
  useEffect(() => {
    if (!WEBCHAT_ID) return undefined;
    if (!document.getElementById(SCRIPT_ID)) {
      window.BOTMAKER_VAR = { ...(window.BOTMAKER_VAR || {}), ...CONTEXTO_CHECKOUT };
      const js = document.createElement('script');
      js.id = SCRIPT_ID;
      js.type = 'text/javascript';
      js.async = true;
      js.src = `https://go.botmaker.com/rest/webchat/p/${encodeURIComponent(WEBCHAT_ID)}/init.js`;
      document.body.appendChild(js);
      // Sin limpieza al desmontar: el widget vive en toda la SPA y se carga una sola vez.
    }
    const tryFind = () => {
      const el = findContainer();
      if (el) {
        setContainer(el);
        obs.disconnect();
      }
    };
    const obs = new MutationObserver(tryFind);
    obs.observe(document.body, { childList: true, subtree: true });
    tryFind();
    return () => obs.disconnect();
  }, []);

  // 2) Posición inicial y reaplicarla ante resize, apertura/cierre del panel o cambios de style
  useEffect(() => {
    if (!container) return undefined;
    const r = container.getBoundingClientRect();
    wantedRef.current = readPos() ?? { x: r.left, y: r.top };
    render(container);

    const reapply = () => render(container);
    window.addEventListener('resize', reapply);
    const ro = new ResizeObserver(reapply);
    ro.observe(container);
    const mo = new MutationObserver(reapply);
    mo.observe(container, { attributes: true, attributeFilter: ['style'] });
    return () => {
      window.removeEventListener('resize', reapply);
      ro.disconnect();
      mo.disconnect();
    };
  }, [container, render]);

  // 3) Arrastre con mouse, touch y lápiz (Pointer Events)
  const onPointerDown = (e) => {
    if (!shownRef.current) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { dx: e.clientX - shownRef.current.x, dy: e.clientY - shownRef.current.y };
  };

  const onPointerMove = (e) => {
    if (!dragRef.current || !container) return;
    wantedRef.current = clamp({ x: e.clientX - dragRef.current.dx, y: e.clientY - dragRef.current.dy }, container);
    render(container);
  };

  const onPointerUp = (e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    savePos(wantedRef.current);
  };

  // Accesibilidad: mover con las flechas del teclado
  const onKeyDown = (e) => {
    const delta = {
      ArrowLeft: { x: -KEY_STEP, y: 0 },
      ArrowRight: { x: KEY_STEP, y: 0 },
      ArrowUp: { x: 0, y: -KEY_STEP },
      ArrowDown: { x: 0, y: KEY_STEP },
    }[e.key];
    if (!delta || !container || !shownRef.current) return;
    e.preventDefault();
    wantedRef.current = clamp({ x: shownRef.current.x + delta.x, y: shownRef.current.y + delta.y }, container);
    render(container);
    savePos(wantedRef.current);
  };

  if (!container || !shown) return null;

  return createPortal(
    <button
      type="button"
      aria-label="Mover chat (arrastrar o usar las flechas del teclado)"
      title="Arrastrá para mover el chat"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      style={{
        position: 'fixed',
        left: Math.max(0, shown.x - HANDLE_SIZE / 2),
        top: Math.max(0, shown.y - HANDLE_SIZE / 2),
        width: HANDLE_SIZE,
        height: HANDLE_SIZE,
        zIndex: 2147483647,
        borderRadius: '50%',
        border: '1px solid rgba(0,0,0,.15)',
        background: '#fff',
        boxShadow: '0 1px 4px rgba(0,0,0,.25)',
        cursor: 'grab',
        touchAction: 'none', // evita que el gesto haga scroll en el celular
        padding: 0,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      {/* ícono de "agarre" (6 puntos) */}
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        {[2, 6, 10].flatMap((y) =>
          [4, 8].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.2" fill="#555" />)
        )}
      </svg>
    </button>,
    document.body
  );
};

export default BotmakerWebchat;
